import {makeAutoObservable} from "mobx";
import {apiRequest} from "../util/util";

const PORTRAIT_POLL_MS = 3000;
const PORTRAIT_WAIT_MS = 2 * 60 * 1000;

export const MAX_STORY_CHARACTERS = 3;
export const MAX_IN_STORY = 6;  // the engine's limit on one story's cast
export const MAX_SETUP_LENGTH = 2000;

export type PortraitState = 'pending' | 'ready' | 'failed'

export interface Portrait {
    version: number | string
    note: string
    state: PortraitState
    url: string
    createdAt: string
}

// A character as a story holds it: a snapshot taken when they joined
export interface CastMember {
    characterId: string
    name: string
    age: number
    looks: string
    personality: string
    relationship: string
    portrait: string
}

export interface Character extends CastMember {
    portraits: Portrait[]
    usable: boolean  // false for characters past the plan's limit after a downgrade
    createdAt: string
}

export interface CharacterFields {
    name: string
    age: number
    looks: string
    personality: string
    relationship: string
}

export interface CharacterLimits {
    characters: number | null  // null is unlimited
    portraitsPerMonth: number
    portraitsUsed: number
}

export interface CharacterReply {
    success: boolean
    message?: string
    character?: Character
    version?: number | string
}

const FAILED: CharacterReply = {success: false, message: "Something went wrong. Please try again."};

// One call to the reader's characters; a failed call reads as a reader-facing failure
export async function charactersRequest(action: string, body: object = {}): Promise<CharacterReply> {
    try {
        const data = await apiRequest('characters', 'POST', {action, ...body});
        return data || FAILED;
    } catch (e) {
        return FAILED;
    }
}

export function hasPendingPortrait(character: Character) {
    return (character.portraits || []).some((portrait) => portrait.state === 'pending');
}

// The signed-in reader's characters, loaded once per account and shared by every page that needs them
class CharacterStore {
    userId: string | null
    loaded: boolean
    enabled: boolean
    characters: Character[]
    limits: CharacterLimits | null
    watching: boolean
    request: Promise<void> | null  // the list call in flight, shared by everyone who asks meanwhile
    requestUserId: string | null  // the account that call is for

    constructor() {
        this.userId = null
        this.loaded = false
        this.enabled = false
        this.characters = []
        this.limits = null
        this.watching = false
        this.request = null
        this.requestUserId = null
        makeAutoObservable(this, {request: false, requestUserId: false}, {autoBind: true})
    }

    get usable() {
        return this.characters.filter((character) => character.usable !== false)
    }

    get portraitsLeft() {
        return this.limits ? Math.max(0, this.limits.portraitsPerMonth - this.limits.portraitsUsed) : null
    }

    find(characterId: string | undefined) {
        return this.characters.find((character) => character.characterId === characterId)
    }

    // Load once per signed-in account, or again when asked to; signing out clears it
    async load(userId: string | null, again: boolean = false) {
        if (userId !== this.userId) {
            this.reset(userId)
        }
        if (userId && (!this.loaded || again)) {
            await this.refresh()
        }
    }

    reset(userId: string | null) {
        this.userId = userId
        this.loaded = false
        this.enabled = false
        this.characters = []
        this.limits = null
    }

    // a call in flight is shared only by the account it's for; switching accounts starts a fresh one
    refresh() {
        if (!this.request || this.requestUserId !== this.userId) {
            const request: Promise<void> = this.fetchList().finally(() => {
                if (this.request === request) {
                    this.request = null
                }
            })
            this.request = request
            this.requestUserId = this.userId
        }
        return this.request
    }

    async fetchList() {
        const userId = this.userId
        let data
        try {
            data = await apiRequest('characters', 'POST', {action: 'list'})
        } catch (e) {
            data = null
        }
        // a failed first list reads as no characters, so pages don't wait forever; a failed later one changes nothing
        if (userId === this.userId && (data || !this.loaded)) {
            this.apply(data || {})
        }
    }

    apply(data: any) {
        this.loaded = true
        this.enabled = !!data.enabled && data.success !== false
        this.characters = Array.isArray(data.characters) ? data.characters : []
        this.limits = data.limits || null
    }

    setWatching(watching: boolean) {
        this.watching = watching
    }

    // Check back while a portrait is being drawn, for up to two minutes
    async watchPortraits() {
        if (this.watching) {
            return
        }
        this.setWatching(true)
        const startedAt = Date.now()
        try {
            while (this.characters.some(hasPendingPortrait) && Date.now() - startedAt < PORTRAIT_WAIT_MS) {
                await new Promise((resolve) => setTimeout(resolve, PORTRAIT_POLL_MS))
                await this.refresh()
            }
        } finally {
            this.setWatching(false)
        }
    }
}

export const characterStore = new CharacterStore();
